/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
/* global gettext */
import React from 'react'; // eslint-disable-line no-unused-vars
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { List } from 'immutable';

import 'react-datepicker/dist/react-datepicker.css';

import { FORMAT_DATE_BACKEND } from 'utils';
import { clearCourseSelectionCache, setTenantPassSelections } from 'actions/actions';
import { isPending } from 'consts/requestStates';
import Callout from 'components/Callout';
import FormError from 'components/form/FormError';
import SubmitButton from 'components/form/SubmitButton';
import FieldWithError from 'components/form/FieldWithError';
import CourseSelectionList from './courses/CourseSelectionList';


const DatePickerField = props => (
  <DatePicker
    {...props.value ? { selected: moment(props.value) } : {}}
    {...props}
    showMonthDropdown
    showYearDropdown
    dateFormat={FORMAT_DATE_BACKEND}
  />
);
DatePickerField.propTypes = {
  value: PropTypes.string.isRequired,
};

export const isBeforeToday = value =>
  moment(value).isBefore(moment().startOf('day'));

function afterNow(value) {
  return isBeforeToday(value)
    ? gettext('Start date must not be in the past.')
    : undefined;
}

function afterStartValueOrNow(value, allValues) {
  return moment(value).isBefore(moment(allValues.start_date))
    ? gettext('Expiry date must be after start date.')
    : undefined;
}

function zeroTo100(value) {
  return value < 0 || value > 100
    ? gettext('Overage must be between 0 and 100.')
    : undefined;
}

function allowHistoricStartDate(editing) {
  return editing && process.env.DJANGO_DISALLOW_EDIT_PASS_WITH_HISTORIC_START_DATE !== 'True';
}

function getPassTypeOptions(passTypes) {
  return passTypes.map(passType => (
    <option key={passType.get('id')} value={passType.get('idnumber')}>
      {passType.get('name')}
    </option>
  ));
}

export class EditCreateTenantPassForm extends React.PureComponent {
  constructor(props) {
    super(props);
    this.onPassTypeChange = this.onPassTypeChange.bind(this);
  }

  componentDidMount() {
    const {
      passTypes,
      passId,
      tenantPass,
      courses,
      setTenantPassSelections: setTenantPassSelectionsBound,
      change,
      clearCourseSelectionCache: clearCourseSelectionCacheBound,
    } = this.props;
    clearCourseSelectionCacheBound();

    if (!passId && passTypes.count() > 0) {
      const passType = passTypes.first().get('idnumber');
      this.setPassType(passType);
      change('pass_type', passType);
    } else if (tenantPass) {
      // Make sure we disable dependant items when we already have data on this page
      const itemsToSelect = tenantPass.get('courses') || List();
      setTenantPassSelectionsBound(courses, itemsToSelect, true);
    }
  }

  onPassTypeChange(e) {
    this.setPassType(e.currentTarget.value);
  }

  setPassType(passType) {
    const {
      setTenantPassSelections: setTenantPassSelectionsBound,
      courses,
    } = this.props;
    const itemsToSelect = courses.filter(course => (course.get('passes') || List()).includes(passType)).map(course => course.get('id'));
    const itemsToDeselect = courses.filter(course => !(course.get('passes') || List()).includes(passType)).map(course => course.get('id'));
    setTenantPassSelectionsBound(courses, itemsToDeselect, false);
    setTenantPassSelectionsBound(courses, itemsToSelect, true);
  }

  render() {
    const {
      requestState,
      handleSubmit,
      onCancel,
      invalid,
      courses,
      change,
      editing,
      disabled,
      startDateValue,
      passTypes,
      setTenantPassSelections: setTenantPassSelectionsBound,
      courseDependencies,
      courseSelectionCache,
    } = this.props;

    return (
      <div>
        <form onSubmit={handleSubmit}>
          <span className="list-search-container pull-right">
            <label htmlFor="name">{gettext('Pass name')}</label>
            <FormError name="name" requestState={requestState} />
            <Field
              component="input"
              type="text"
              name="name"
              placeholder={gettext('Pass name')}
              props={{ disabled }}
            />

            <label htmlFor="licenses">{gettext('Number of licenses')}</label>
            <FormError name="licenses" requestState={requestState} />
            <Field
              component="input"
              type="number"
              name="licenses"
              placeholder={gettext('Number of licenses')}
              props={{ disabled }}
            />

            <label htmlFor="license_overage">{gettext('Overage (% of licences)')}</label>
            <FormError name="license_overage" requestState={requestState} />
            <Field
              component={FieldWithError}
              innerComponent="input"
              type="number"
              name="license_overage"
              placeholder={gettext('Overage')}
              props={{ disabled }}
              validate={[zeroTo100]}
            />

            <label htmlFor="start_date">{gettext('Start date')}</label>
            <FormError name="start_date" requestState={requestState} />
            <Field
              component={FieldWithError}
              innerComponent={DatePickerField}
              name="start_date"
              placeholderText={gettext('Start date')}
              props={{ disabled, minDate: allowHistoricStartDate(editing) ? undefined : moment() }}
              validate={allowHistoricStartDate(editing) ? [] : [afterNow]}
            />

            <label htmlFor="end_date">{gettext('Expiry date')}</label>
            <FormError name="end_date" requestState={requestState} />
            <Field
              component={FieldWithError}
              innerComponent={DatePickerField}
              name="end_date"
              placeholderText={gettext('End date')}
              props={{ disabled, minDate: moment(startDateValue) }}
              validate={[afterStartValueOrNow]}
            />

            <label htmlFor="pass_type">{gettext('Pass Type')}</label>
            <Field
              component={FieldWithError}
              innerComponent="select"
              name="pass_type"
              props={{ disabled }}
              onChange={this.onPassTypeChange}
            >
              {getPassTypeOptions(passTypes)}
            </Field>

            <label htmlFor="notes">{gettext('Notes (optional)')}</label>
            <FormError name="name" requestState={requestState} />
            <Field
              component="textarea"
              type="text"
              name="notes"
              placeholder={gettext('Notes')}
              props={{ disabled }}
            />

            {courses.size ? (
              <CourseSelectionList
                courses={courses}
                title={gettext('Select courses:')}
                change={change}
                disabled={disabled}
                setTenantPassSelections={setTenantPassSelectionsBound}
                courseSelectionCache={courseSelectionCache}
                courseDependencies={courseDependencies}
              />
            ) : (
              <Callout className="alert">
                {gettext('No courses available')}
              </Callout>
            )}

            <FormError name="non_field_errors" requestState={requestState} />

            <button
              type="button"
              className="button secondary"
              onClick={onCancel}
              disabled={isPending(requestState)}
            >
              {gettext('Cancel')}
            </button>

            <SubmitButton
              disabled={disabled}
              valid={!invalid}
              onClick={handleSubmit}
              pending={isPending(requestState)}
              pendingText={editing ? gettext('Updating...') : gettext('Creating...')}
            >
              {editing ? gettext('Update') : gettext('Create')}
            </SubmitButton>
          </span>
        </form>
      </div>
    );
  }
}

EditCreateTenantPassForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  requestState: ImmutablePropTypes.map.isRequired,
  courses: ImmutablePropTypes.list.isRequired,
  invalid: PropTypes.bool.isRequired,
  change: PropTypes.func.isRequired,
  editing: PropTypes.bool,
  disabled: PropTypes.bool,
  startDateValue: PropTypes.string,
  passTypes: ImmutablePropTypes.list.isRequired,
  passId: PropTypes.string,
  setTenantPassSelections: PropTypes.func.isRequired,
  courseSelectionCache: ImmutablePropTypes.map.isRequired,
  courseDependencies: ImmutablePropTypes.map.isRequired,
  tenantPass: ImmutablePropTypes.map,
  clearCourseSelectionCache: PropTypes.func.isRequired,
};
EditCreateTenantPassForm.defaultProps = {
  editing: false,
  disabled: false,
  startDateValue: '',
  passId: null,
  tenantPass: null,
};

export const CREATE_TENANT_PASS_FORM = 'createTenantPass';

const EditCreateTenantPassReduxForm = reduxForm({
  form: CREATE_TENANT_PASS_FORM,
})(EditCreateTenantPassForm);

const selector = formValueSelector(CREATE_TENANT_PASS_FORM);

function mapStateToProps(state) {
  return {
    startDateValue: selector(state, 'start_date'),
    courseSelectionCache: state.courseSelectionCache,
    courseDependencies: state.courseDependencies,
  };
}

export default connect(mapStateToProps, {
  setTenantPassSelections,
  clearCourseSelectionCache,
})(EditCreateTenantPassReduxForm);
